/**
 * Pulisce tutti i dati offline locali al logout:
 * - database SQLite (wa-sqlite / OPFS o fallback IndexedDB)
 * - sessione UI (localforage b2b_session)
 * - chiavi localStorage usate da sync PWA / ordine
 * - IndexedDB backup critico (pre-recovery)
 * - stato redux-persist (carrello, bozze, impostazioni, auth persistito)
 *
 * Al redirect verso /login il worker SQLite NON va resettato sulla pagina corrente:
 * si imposta un flag e il wipe OPFS/IDB avviene al boot (WorkerHealthGate) per evitare
 * race su Access Handles OPFS tra cleanup e reinizializzazione.
 */

import { dbBridge } from '../../orm/worker/db-bridge';
import { db } from '../../orm/connection';
import { sessionStateService } from './session-state.service';
import { persistor } from '../../stores';
import { debugWarn } from '../../utils/debug';
import {
  DEFAULT_OFFLINE_DB_NAME,
  deleteCriticalBackupIndexedDb,
  deleteIdbVfsDatabase,
  deleteOpfsSqliteDirectory,
} from './storage-reset';

/** Session flag: al prossimo avvio eseguire prepareDatabaseAfterLogout prima di init DB. */
export const POST_LOGOUT_DB_CLEANUP_KEY = 'b2b_post_logout_db_cleanup';

export function markPostLogoutDatabaseCleanup(): void {
  try {
    sessionStorage.setItem(POST_LOGOUT_DB_CLEANUP_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function shouldRunPostLogoutDatabaseCleanup(): boolean {
  try {
    return sessionStorage.getItem(POST_LOGOUT_DB_CLEANUP_KEY) === '1';
  } catch {
    return false;
  }
}

function clearPostLogoutDatabaseCleanupFlag(): void {
  try {
    sessionStorage.removeItem(POST_LOGOUT_DB_CLEANUP_KEY);
  } catch {
    /* ignore */
  }
}

const PWA_LS_KEYS = [
  'pwa_last_route',
  'pwa_last_auto_sync_date',
  'pwa_last_seen',
] as const;

const DB_CLOSE_TIMEOUT_MS = 4_000;
const FULL_RESET_TIMEOUT_MS = 8_000;
const SESSION_CLEAR_TIMEOUT_MS = 2_500;
const CRITICAL_IDB_DELETE_MAX_MS = 4_000;
const PERSISTOR_PURGE_TIMEOUT_MS = 3_000;

async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T | 'timeout'> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), ms);
  });
  try {
    const result = await Promise.race([promise, timeout]);
    if (timer) clearTimeout(timer);
    if (result === 'timeout') {
      debugWarn(`[clearOfflineUserData] ${label} timed out after ${ms}ms`);
      return 'timeout';
    }
    return result as T;
  } catch (e) {
    if (timer) clearTimeout(timer);
    throw e;
  }
}

function clearLogoutLocalStorageKeys(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    for (const k of PWA_LS_KEYS) {
      localStorage.removeItem(k);
    }
    localStorage.removeItem('orderModeState');
    localStorage.removeItem('forceDbReset');
    localStorage.removeItem('pwa_update_flow_state');
  } catch {
    /* ignore */
  }
}

/**
 * Wipe storage dal main thread (senza worker). Eseguire al boot post-logout
 * prima di db.initialize per liberare OPFS/IDB lasciati da un worker terminato in modo brusco.
 */
export async function prepareDatabaseAfterLogout(): Promise<void> {
  dbBridge.forceKillWorker();
  clearPostLogoutDatabaseCleanupFlag();

  await Promise.all([
    deleteOpfsSqliteDirectory(),
    deleteIdbVfsDatabase(DEFAULT_OFFLINE_DB_NAME),
    deleteCriticalBackupIndexedDb(),
  ]);

  try {
    const outcome = await withTimeout(
      sessionStateService.clear(),
      SESSION_CLEAR_TIMEOUT_MS,
      'sessionState.clear (post-logout boot)',
    );
    if (outcome === 'timeout') {
      debugWarn('[prepareDatabaseAfterLogout] sessionState.clear timed out');
    }
  } catch (e) {
    debugWarn('[prepareDatabaseAfterLogout] sessionState:', e);
  }

  clearLogoutLocalStorageKeys();
}

export async function clearOfflineUserData(): Promise<void> {
  dbBridge.forceKillWorker();

  // 1. Chiudi il DB — se il worker è bloccato, forza kill dopo timeout
  const closeOutcome = await withTimeout(
    Promise.resolve().then(() => db.close()),
    DB_CLOSE_TIMEOUT_MS,
    'db.close',
  );
  if (closeOutcome === 'timeout') {
    dbBridge.forceKillWorker();
  }

  // 2. Reset SQLite (parallelo con pulizia sessione)
  const resetPromise = (async (): Promise<void> => {
    try {
      const outcome = await withTimeout(
        dbBridge.fullReset('b2b_offline'),
        FULL_RESET_TIMEOUT_MS,
        'dbBridge.fullReset',
      );
      if (outcome === 'timeout') {
        dbBridge.forceKillWorker();
      }
    } catch (e) {
      debugWarn('[clearOfflineUserData] SQLite fullReset:', e);
      dbBridge.forceKillWorker();
    }
  })();

  // 3. Sessione UI (parallela con reset SQLite)
  const sessionPromise = (async (): Promise<void> => {
    try {
      const outcome = await withTimeout(
        sessionStateService.clear(),
        SESSION_CLEAR_TIMEOUT_MS,
        'sessionState.clear',
      );
      if (outcome === 'timeout') {
        debugWarn('[clearOfflineUserData] sessionState.clear gave up after timeout');
      }
    } catch (e) {
      debugWarn('[clearOfflineUserData] sessionState:', e);
    }
  })();

  clearLogoutLocalStorageKeys();

  await Promise.all([resetPromise, sessionPromise]);

  await deleteCriticalBackupIndexedDb();

  // 6. Purge redux-persist (auth, carrello, bozze)
  try {
    const outcome = await withTimeout(
      persistor.purge(),
      PERSISTOR_PURGE_TIMEOUT_MS,
      'persistor.purge',
    );
    if (outcome === 'timeout') {
      debugWarn('[clearOfflineUserData] persistor.purge timed out — auth state may persist until next reload');
    }
  } catch (e) {
    debugWarn('[clearOfflineUserData] persistor.purge:', e);
  }
}

/**
 * Wipe completo per flusso invito: localStorage, sessionStorage e tutti i dati
 * offline/persistiti (SQLite, IndexedDB, redux-persist).
 * Usare all'apertura di /auth/accept-invitation prima della verifica token.
 */
export async function clearAllLocalData(): Promise<void> {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.clear();
    } catch {
      /* ignore */
    }
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
  }
  await clearOfflineUserData();
}
